import React, { useState, useEffect } from 'react';

const DEFAULT_CONFIG = {
  adminEmail: '',
  adminName: 'ParentsProtection Support',
  emailSubject: 'Your New Password — ParentsProtection',
  emailTemplate: `Hi {customer_name},

Your password has been reset successfully.

Your new password: {password}

Please login at: https://www.parentsprotection.org/dashboard

For security, we recommend changing your password after logging in.

Thanks,
{admin_name}
ParentsProtection Team`,
  whatsappTemplate: `Hi {customer_name}, your ParentsProtection password has been reset.\n\nNew password: *{password}*\n\nLogin: https://www.parentsprotection.org/dashboard\n\nPlease change your password after logging in.`,
};

export function getAdminConfig() {
  try {
    const saved = localStorage.getItem('pg_admin_config');
    if (saved) return { ...DEFAULT_CONFIG, ...JSON.parse(saved) };
  } catch {}
  return DEFAULT_CONFIG;
}

export function saveAdminConfig(config) {
  localStorage.setItem('pg_admin_config', JSON.stringify(config));
}

export function buildEmailLink(customerEmail, customerName, password) {
  const config = getAdminConfig();
  const body = config.emailTemplate
    .replace(/{customer_name}/g, customerName || 'Customer')
    .replace(/{password}/g, password)
    .replace(/{admin_name}/g, config.adminName);
  const subject = config.emailSubject;
  return `mailto:${customerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function buildWhatsAppLink(phone, customerName, password) {
  const config = getAdminConfig();
  const msg = config.whatsappTemplate
    .replace(/{customer_name}/g, customerName || 'Customer')
    .replace(/{password}/g, password)
    .replace(/{admin_name}/g, config.adminName);
  const cleanPhone = (phone || '').replace(/\D/g, '');
  return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`;
}

function ChangePasswordCard({ onToast }) {
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setErr('');
    if (newPw.length < 8) { setErr('New password must be at least 8 characters'); return; }
    if (newPw !== confirmPw) { setErr('New passwords do not match'); return; }
    if (newPw === oldPw) { setErr('New password must be different from current'); return; }
    setBusy(true);
    try {
      const r = await fetch('/api/admin-change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oldPassword: oldPw, newPassword: newPw }),
      });
      if (r.ok) {
        // Update stored Bearer token so existing session stays valid.
        localStorage.setItem('pg_admin_token', newPw);
        setOldPw(''); setNewPw(''); setConfirmPw('');
        onToast('Password changed successfully');
      } else {
        const msg = await r.json().catch(() => ({}));
        setErr(msg.error || `Change failed (HTTP ${r.status})`);
      }
    } catch {
      setErr('Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: 'linear-gradient(135deg, #ef4444, #f97316)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18,
        }}>🔐</div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>Change Admin Password</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>Update the password used to log into this admin panel</div>
        </div>
      </div>

      <form onSubmit={submit}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
              Current Password
            </label>
            <input type="password" value={oldPw} onChange={e => setOldPw(e.target.value)}
              autoComplete="current-password" placeholder="••••••••" style={{ width: '100%' }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
              New Password
            </label>
            <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
              autoComplete="new-password" placeholder="At least 8 characters" style={{ width: '100%' }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
              Confirm New Password
            </label>
            <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
              autoComplete="new-password" placeholder="Re-type new password" style={{ width: '100%' }} />
          </div>
        </div>

        {err && (
          <div style={{ color: '#dc2626', background: '#fee2e2', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>
            {err}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button type="submit" className="btn" disabled={busy} style={{ marginTop: 0 }}>
            {busy ? 'Changing…' : 'Change Password'}
          </button>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>
            The new password takes effect immediately. You'll stay logged in on this device.
          </div>
        </div>
      </form>
    </div>
  );
}

export default function AdminSettings() {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [toast, setToast] = useState('');
  const [previewMode, setPreviewMode] = useState('email'); // 'email' or 'whatsapp'

  useEffect(() => {
    setConfig(getAdminConfig());
  }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleSave = () => {
    saveAdminConfig(config);
    showToast('Settings saved!');
  };

  const handleReset = () => {
    setConfig(DEFAULT_CONFIG);
    saveAdminConfig(DEFAULT_CONFIG);
    showToast('Settings reset to defaults');
  };

  // Preview with sample data
  const previewEmail = config.emailTemplate
    .replace(/{customer_name}/g, 'Priya Sharma')
    .replace(/{password}/g, 'abc12345')
    .replace(/{admin_name}/g, config.adminName);

  const previewWhatsApp = config.whatsappTemplate
    .replace(/{customer_name}/g, 'Priya Sharma')
    .replace(/{password}/g, 'abc12345')
    .replace(/{admin_name}/g, config.adminName);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 className="page-title" style={{ marginBottom: 0 }}>Admin Settings</h1>
        <button className="btn" style={{ marginTop: 0 }} onClick={handleSave}>
          Save Settings
        </button>
      </div>

      {/* Change Admin Password */}
      <ChangePasswordCard onToast={showToast} />

      {/* Email Configuration */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18,
          }}>✉️</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>Email Configuration</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>Configure email used for password resets & customer communication</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16, marginBottom: 20 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
              Admin Email Address
            </label>
            <input type="email" value={config.adminEmail}
              onChange={e => setConfig(c => ({ ...c, adminEmail: e.target.value }))}
              placeholder="your@email.com"
              style={{ width: '100%' }} />
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              Password reset emails will be sent from this address
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
              Display Name
            </label>
            <input type="text" value={config.adminName}
              onChange={e => setConfig(c => ({ ...c, adminName: e.target.value }))}
              placeholder="ParentsProtection Support"
              style={{ width: '100%' }} />
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              Name shown in email signature
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
            Email Subject Line
          </label>
          <input type="text" value={config.emailSubject}
            onChange={e => setConfig(c => ({ ...c, emailSubject: e.target.value }))}
            placeholder="Your New Password — ParentsProtection"
            style={{ width: '100%' }} />
        </div>
      </div>

      {/* Templates */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, background: '#fef3c7',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
          }}>📝</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>Message Templates</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Use variables: <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>{'{customer_name}'}</code>{' '}
              <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>{'{password}'}</code>{' '}
              <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>{'{admin_name}'}</code>
            </div>
          </div>
        </div>

        {/* Tab toggle */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          <button className={`btn-sm ${previewMode === 'email' ? 'active' : ''}`}
            onClick={() => setPreviewMode('email')}
            style={{
              background: previewMode === 'email' ? '#0f172a' : undefined,
              color: previewMode === 'email' ? '#fff' : undefined,
            }}>
            ✉️ Email Template
          </button>
          <button className={`btn-sm ${previewMode === 'whatsapp' ? 'active' : ''}`}
            onClick={() => setPreviewMode('whatsapp')}
            style={{
              background: previewMode === 'whatsapp' ? '#0f172a' : undefined,
              color: previewMode === 'whatsapp' ? '#fff' : undefined,
            }}>
            💬 WhatsApp Template
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
          {/* Editor */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
              {previewMode === 'email' ? 'Email Body' : 'WhatsApp Message'}
            </label>
            <textarea
              rows={12}
              value={previewMode === 'email' ? config.emailTemplate : config.whatsappTemplate}
              onChange={e => setConfig(c => ({
                ...c,
                [previewMode === 'email' ? 'emailTemplate' : 'whatsappTemplate']: e.target.value
              }))}
              style={{ width: '100%', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}
            />
          </div>

          {/* Preview */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
              Preview (sample data)
            </label>
            <div style={{
              background: previewMode === 'email' ? '#fff' : '#dcfce7',
              border: `1px solid ${previewMode === 'email' ? '#e2e8f0' : '#bbf7d0'}`,
              borderRadius: 10, padding: '16px 20px', fontSize: 13, lineHeight: 1.7,
              whiteSpace: 'pre-wrap', color: '#374151', minHeight: 200,
              fontFamily: previewMode === 'email' ? 'inherit' : 'inherit',
            }}>
              {previewMode === 'email' ? (
                <>
                  <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: 10, marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>From: {config.adminEmail || 'your@email.com'}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>To: priya@example.com</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>Subject: {config.emailSubject}</div>
                  </div>
                  {previewEmail}
                </>
              ) : (
                <>
                  <div style={{ fontSize: 11, color: '#16a34a', marginBottom: 8, fontWeight: 600 }}>
                    WhatsApp to: +91 98765 43210
                  </div>
                  {previewWhatsApp}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, background: '#f0f9ff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
          }}>💡</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>How Password Reset Works</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>Step-by-step flow</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          {[
            { step: '1', icon: '🔑', title: 'Customer Requests', desc: 'Customer clicks "Forgot Password" on login page and submits their email' },
            { step: '2', icon: '🔔', title: 'You Get Notified', desc: 'Request appears in Password Resets page with pending status' },
            { step: '3', icon: '🔄', title: 'You Reset Password', desc: 'Click "Reset Password", generate a new one, then click "Reset & Send Email"' },
            { step: '4', icon: '✅', title: 'Customer Gets Email', desc: 'Email opens in your mail app with new password pre-filled, ready to send' },
          ].map(s => (
            <div key={s.step} style={{
              background: '#f8fafc', borderRadius: 10, padding: '16px 14px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', marginBottom: 4 }}>
                Step {s.step}: {s.title}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        <button className="btn" onClick={handleSave}>Save Settings</button>
        <button className="btn-sm" onClick={handleReset}
          style={{ background: '#fee2e2', color: '#dc2626' }}>
          Reset to Defaults
        </button>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
